'use client';

import clsx from 'clsx';
import { usePathname } from 'next/navigation';

import { useTranslations } from 'next-intl';

import { ImageClient, Link, RichText, Typography } from '@/components/core';
import BitCarousel from '@/components/core/BitCarousel/BitCarousel';
import { LocalizedPrice } from '@/components/storyblok-components/LocalizedPrice/LocalizedPrice';
import ShinyText from '@/components/ui/ShinyText/ShinyText';
import { LocationStoryblok } from '@/types/storyblok/storyblok-types';
import { nanoid } from '@/utils/helper';

import styles from './LocationDetails.module.scss';

type LocationDetailsProps = {
    location: { content: LocationStoryblok } & any;
    className?: string;
    hideCityName?: boolean;
};

export const LocationDetails: React.FC<LocationDetailsProps> = ({ location, className, hideCityName = false }) => {
    const { slug, content } = location;
    const { name, types, description, cover, longTermRoi, shortTermRoi, appreciation, city } = content || {};
    const pathname = usePathname();
    const t = useTranslations('LocationDetails');
    const hasTypes = Array.isArray(types) && types.length > 0;

    const smallestPrice = hasTypes ? Math.min(...types.map(v => Number(v.startingPrice))) : 0;
    const biggestPrice = hasTypes ? Math.max(...types.map(v => Number(v.endingPrice))) : 0;

    const discountPercentages = hasTypes ? types.map(v => Number(v.discountOffPlanPercentage)) : [];
    const smallestDiscount = discountPercentages.length ? Math.min(...discountPercentages) : 0;

    const rawUnitInfos = [
        {
            label: t('unit.types'),
            value: hasTypes ? types.length : 0,
            firstColumn: <h5>{t('unit.types')}</h5>,
            secondColumn: (
                <div className={styles.typesRow}>
                    {types?.map(el => (
                        <small
                            key={`type-${el.name}`}
                            className={styles.typeEl}
                        >
                            {el.name}
                        </small>
                    ))}
                </div>
            ),
        },
        {
            label: t('unit.cheapestFrom'),
            value: smallestPrice,
            firstColumn: <h5>{t('unit.cheapestFrom')}</h5>,
            secondColumn: (
                <div className={styles.typeEl}>
                    <LocalizedPrice
                        price={smallestPrice}
                        defaultCurrency={city.content.currency}
                    />
                </div>
            ),
        },
        {
            label: t('unit.mostExpensive'),
            value: biggestPrice,
            firstColumn: <h5>{t('unit.mostExpensive')}</h5>,
            secondColumn: (
                <div className={styles.typeEl}>
                    <LocalizedPrice
                        price={biggestPrice}
                        defaultCurrency={city.content.currency}
                    />
                </div>
            ),
        },
    ];

    const unitInfos = rawUnitInfos.filter(row => row.value);

    const rawRoiInfos = [
        { label: t('roi.longTerm'), value: longTermRoi, suffix: '%' },
        { label: t('roi.shortTerm'), value: shortTermRoi, suffix: '%' },
        { label: t('roi.appreciation'), value: appreciation, suffix: '%' },
        { label: t('roi.offPlanDiscountFrom'), value: smallestDiscount, suffix: '%' },
    ];

    const roiInfos = rawRoiInfos
        .filter(info => info.value)
        .map(info => ({
            firstColumn: <h5>{info.label}</h5>,
            secondColumn: (
                <div className={styles.typeEl}>
                    {info.value}
                    {info.suffix}
                </div>
            ),
        }));

    return (
        <div className={clsx(styles.root, className)}>
            <div className={styles.list}>
                {!hideCityName && (
                    <div>
                        <Typography
                            looksLike="pLarge"
                            className="bold"
                        >
                            {name}
                        </Typography>
                    </div>
                )}

                <div>
                    <div>
                        <RichText
                            looksLike="pSmall"
                            document={description}
                            classNameRoot={styles.description}
                        />
                    </div>

                    <div className={styles.main}>
                        <BitCarousel
                            autoplay={types?.length > 1}
                            pauseOnHover
                            baseWidth={320}
                            loop={types?.length > 1}
                            autoplayDelay={3000}
                            items={types?.map(item => ({
                                title: item.name,
                                description: t('card.description'),
                                content: item?.startingPrice && (
                                    <div className={styles.cardContent}>
                                        <div>
                                            <div className={styles.pre}>{t('card.starting')}</div>
                                            <span className={styles.start}>
                                                <LocalizedPrice
                                                    price={item.startingPrice}
                                                    defaultCurrency={city.content.currency}
                                                />
                                            </span>
                                        </div>
                                        {item.endingPrice && (
                                            <div>
                                                <div className={styles.pre}>{t('card.until')}</div>
                                                <span className={styles.end}>
                                                    <LocalizedPrice
                                                        price={item.endingPrice}
                                                        defaultCurrency={city.content.currency}
                                                    />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ),
                                icon: (
                                    <div className={styles.cardImage}>
                                        <ImageClient
                                            height={180}
                                            width={180}
                                            focalPoints={item?.image?.focus}
                                            src={item?.image?.filename}
                                            alt={item?.image?.alt}
                                        />
                                    </div>
                                ),
                            }))}
                        />

                        <div className={styles.contentWrap}>
                            <div className={styles.gradient} />

                            {cover?.filename && (
                                <ImageClient
                                    className={styles.image}
                                    src={cover.filename}
                                    alt={cover.alt}
                                    width={1000}
                                    height={360}
                                    focalPoints={cover.focus}
                                />
                            )}

                            <div className={styles.tableWrap}>
                                <div
                                    className={clsx(styles.tables, {
                                        [styles.full]: unitInfos.length > 0 && roiInfos.length > 0,
                                    })}
                                >
                                    {unitInfos.length > 0 && (
                                        <table>
                                            <tbody>
                                                {unitInfos.map(part => (
                                                    <tr key={nanoid()}>
                                                        <td>{part.firstColumn}</td>
                                                        <td>{part.secondColumn}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    {roiInfos.length > 0 && (
                                        <table>
                                            <tbody>
                                                {roiInfos.map(part => (
                                                    <tr key={nanoid()}>
                                                        <td>{part.firstColumn}</td>
                                                        <td>{part.secondColumn}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>

                            <div className={styles.mobileInfos}>
                                {unitInfos.length > 0 && (
                                    <div>
                                        {unitInfos.map(part => (
                                            <div key={nanoid()}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {part.firstColumn}
                                                </div>
                                                <div
                                                    className={styles.spec}
                                                    style={{
                                                        margin: '8px 0 16px',
                                                    }}
                                                >
                                                    {part.secondColumn}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.actions}>
                <Link
                    className={clsx('solidButton', styles.button)}
                    href={`${pathname}${slug}`}
                >
                    <ShinyText
                        text={t('action.visitLocation')}
                        disabled={false}
                        speed={3}
                    />
                </Link>
            </div>
        </div>
    );
};
